import { useEffect, useState } from 'react';
import type {
    AllTreatmentsItem,
    OsasAllTreatmentsData,
    TreatmentData,
} from '@common/validation/customControls/osas/osasAllTreatmentsSchemas/types';
import type { TreatmentChoice } from '@common/validation/customControls/osas/osasEvaluationSchemas/types';
import type { OsasTreatmentChoiceCustomControlData } from '@common/validation/customControls/osas/osasTreatmentChoice/types';
import { CustomControlCode, OsasTrialPeriod, ServiceCode } from '@common/enums';
import { findSingleProcessByServiceCode, getLastProcessActivityByCustomControlCodes } from '@common/utils/processUtil';
import { TreatmentStatus } from '@common/validation/customControls/osas/osasAllTreatmentsSchemas/types';
import { usePatientEpisodeCategoryContext } from './usePatientEpisodeCategoryContext';

type OsasallTreatmentsOptions = {
    onChange: (data: Partial<OsasTreatmentChoiceCustomControlData>, validateKeys?: string | string[]) => void;
};

export function useOsasAllTreatments(options: OsasallTreatmentsOptions) {
    const { onChange } = options;
    const { processes } = usePatientEpisodeCategoryContext();

    const [initialAllTreatments, setInitialAllTreatments] = useState<OsasAllTreatmentsData>([]);
    const [allTreatments, setAllTreatments] = useState<OsasAllTreatmentsData>([]);
    const [inProgressTreatments, setInProgressTreatments] = useState<TreatmentData>();
    const [hasInProgressTreatments, setHasInProgressTreatments] = useState(false);

    useEffect(() => {
        getAllTreatments();
    }, [processes]);

    useEffect(() => {
        onChange({ allTreatments });
    }, [allTreatments]);

    useEffect(() => {
        if (inProgressTreatments) {
            setHasInProgressTreatments(Object.values(inProgressTreatments).some((value) => value));
        }
    }, [inProgressTreatments]);

    function getAllTreatments() {
        const process = findSingleProcessByServiceCode(processes, ServiceCode.OSAS);

        if (!process) {
            return;
        }

        const lastCompletedActivityWithTreatment = getLastProcessActivityByCustomControlCodes(process, [
            CustomControlCode.OSASConsult,
            CustomControlCode.OSASTreatmentChoice,
            CustomControlCode.OSASFollowUpConsult,
            CustomControlCode.OSASEvaluation,
        ]);

        const allTreatmentsFromLastActivity =
            (lastCompletedActivityWithTreatment?.customControlData?.allTreatments as OsasAllTreatmentsData) ?? [];

        setInitialAllTreatments(allTreatmentsFromLastActivity);
        setAllTreatments(allTreatmentsFromLastActivity);
        setInProgressTreatments(transformToTreatmentData(allTreatmentsFromLastActivity));
    }

    function onChangeTreatment(data?: Partial<TreatmentData>) {
        if (!data) {
            return;
        }

        let newAllTreatments = [...allTreatments];

        // adding new treatments or updating classification choice
        Object.entries(data).forEach(([key, value]) => {
            if (value) {
                const treatmentToUpdate = allTreatments.find((t) => t.treatmentName === key && t.endDate === null);

                if (treatmentToUpdate) {
                    newAllTreatments = newAllTreatments.map((item) => {
                        if (item.treatmentName === key && item.endDate === null) {
                            return getUpdatedTreatment(treatmentToUpdate, value);
                        }

                        return item;
                    });
                } else {
                    newAllTreatments.push(generateNewTreatment(key as keyof TreatmentData, value));
                }
            } else {
                // valud is false, so we need to revert changes
                const treatmentIsStopped = allTreatments.some(
                    (item) => item.treatmentName === key && item.status === TreatmentStatus.stopped
                );

                if (treatmentIsStopped) {
                    return;
                }

                const treatmentInInitialList = initialAllTreatments.find(
                    (item) => item.treatmentName === key && item.endDate === null
                );

                if (treatmentInInitialList) {
                    newAllTreatments = newAllTreatments.map((item) => {
                        if (item.treatmentName === key && item.endDate === null) {
                            return treatmentInInitialList;
                        }

                        return item;
                    });
                }

                newAllTreatments = newAllTreatments.filter(
                    (item) => item.treatmentName !== key || item.startDate !== null
                );
            }
        });

        setAllTreatments(newAllTreatments);
    }

    function onBreakTreatments(treatmentsToBreak: Array<keyof TreatmentData>) {
        let newAllTreatments = [...allTreatments];

        for (const initialTreatment of initialAllTreatments) {
            newAllTreatments = newAllTreatments.map((treatment) => {
                if (
                    treatment.treatmentName === initialTreatment.treatmentName &&
                    treatment.startDate === initialTreatment.startDate &&
                    treatment.classification === initialTreatment.classification
                ) {
                    if (treatmentsToBreak.includes(initialTreatment.treatmentName)) {
                        return {
                            ...treatment,
                            status: TreatmentStatus.stopped,
                        };
                    }
                    if (initialTreatment.endDate === null) {
                        return {
                            ...initialTreatment,
                        };
                    }

                    return treatment;
                }

                return treatment;
            });
        }

        setAllTreatments(newAllTreatments);
    }

    function onChangeTrialPeriod(treatmentsToChange: TreatmentChoice, trialPeriod: OsasTrialPeriod) {
        let newAllTreatments = [...allTreatments];

        for (const initialTreatment of initialAllTreatments) {
            if (
                (initialTreatment.treatmentName === 'CPAP' && treatmentsToChange['PAP']) ||
                treatmentsToChange[initialTreatment.treatmentName as keyof TreatmentChoice]
            ) {
                newAllTreatments = newAllTreatments.map((treatment) => {
                    if (
                        treatment.treatmentName === initialTreatment.treatmentName &&
                        treatment.startDate === initialTreatment.startDate &&
                        treatment.endDate === null &&
                        treatment.classification === initialTreatment.classification
                    ) {
                        return {
                            ...treatment,
                            status: calculateStatusFromTrialPeriod(trialPeriod),
                        };
                    }

                    return treatment;
                });
            }
        }

        setAllTreatments(newAllTreatments);
    }

    function getUpdatedTreatment(treatmentToUpdate: AllTreatmentsItem, value: string | boolean): AllTreatmentsItem {
        if (value && ['CPAP', 'MRA', 'SPT'].includes(treatmentToUpdate.treatmentName) && typeof value === 'string') {
            return { ...treatmentToUpdate, classification: value, status: TreatmentStatus.inProgress };
        }

        return { ...treatmentToUpdate, status: TreatmentStatus.inProgress };
    }

    function generateNewTreatment(treatmentName: keyof TreatmentData, value: string | boolean) {
        return {
            classification: ['CPAP', 'MRA', 'SPT'].includes(treatmentName) && typeof value === 'string' ? value : null,
            endDate: null,
            startDate: null,
            status: TreatmentStatus.inProgress,
            treatmentName: treatmentName,
        };
    }

    return {
        allTreatments,
        hasInProgressTreatments,
        inProgressTreatments,
        onBreakTreatments,
        onChangeTreatment,
        onChangeTrialPeriod,
    };
}

function transformToTreatmentData(osasData: OsasAllTreatmentsData): TreatmentData {
    const treatmentData: TreatmentData = {
        CPAP: false,
        MRA: false,
        SPT: false,
        lifestyle: false,
        other: false,
        refer: false,
    };

    Object.entries(osasData).forEach(([_, value]) => {
        if (
            (value.status === TreatmentStatus.inProgress && value.startDate !== null) ||
            (value.status === TreatmentStatus.stopped && value.endDate === null)
        ) {
            treatmentData[value.treatmentName] = true;
        }
    });

    return treatmentData;
}

function calculateStatusFromTrialPeriod(trialPeriod: OsasTrialPeriod): TreatmentStatus {
    switch (trialPeriod) {
        case OsasTrialPeriod.stop:
            return TreatmentStatus.stopped;
        case OsasTrialPeriod.successful:
            return TreatmentStatus.completed;
        default:
            return TreatmentStatus.inProgress;
    }
}
